import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { ReplacementCostLand } from "./replacement-cost-land.types";

@Injectable({
    providedIn: 'root'
})
export class ReplacementCostLandService {
    // Private
    private _replacementCostLand: BehaviorSubject<ReplacementCostLand | null> = new BehaviorSubject(null);
    private _replacementCostLands: BehaviorSubject<ReplacementCostLand[] | null> = new BehaviorSubject(null);
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for builtUpArea
     */
    get replacementCostLand$(): Observable<ReplacementCostLand> {
        return this._replacementCostLand.asObservable();
    }

    /**
     * Getter for builtUpAreas
     */
    get replacementCostLands$(): Observable<ReplacementCostLand[]> {
        return this._replacementCostLands.asObservable();
    }

    /**
     * Get builtUpAreas
     */
    getReplacementCostLands(reportId: number): Observable<BaseRespone<ReplacementCostLand[]>> {
        return this._httpClient.get<BaseRespone<ReplacementCostLand[]>>(`${environment.baseUrl}/Report/GetReplacementCostLandslist/${reportId}`).pipe(
            tap((response) => {
                this._replacementCostLands.next(response.result);
            })
        );
    }

    /**
      * Create builtUpArea
      */
    createReplacementCostLand(builtUpArea: ReplacementCostLand): Observable<BaseRespone<ReplacementCostLand>> {
        return this.replacementCostLands$.pipe(
            take(1),
            switchMap(replacementCostLands => this._httpClient.post<BaseRespone<ReplacementCostLand>>(`${environment.baseUrl}/Report/AddReplacementCostLands`, builtUpArea)
                .pipe(
                    catchError((error) => {
                        return of(error.error);
                    }),
                    map((response) => {
                        // Return the new product
                        if (response.isSuccess) {
                            this._replacementCostLands.next([response.result, ...replacementCostLands]);

                        }
                        return response;
                    })
                ))
        );
    }

    /**
     * Get board
     *
     * @param id
     */
    getReplacementCostLand(id: number): Observable<BaseRespone<ReplacementCostLand>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetReplacementCostLands/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._replacementCostLand.next(board.result))
        );
    }


    /**
    * Get contact by id
    */
    getReplacementCostLandById(id: number): Observable<ReplacementCostLand> {
        return this._replacementCostLands.pipe(
            take(1),
            map((replacementCostLands) => {

                // Find the contact
                const replacementCostLand = replacementCostLands.find(item => item.id === id) || null;

                // Update the replacementCostLand
                this._replacementCostLand.next(replacementCostLand);

                // Return the replacementCostLand
                return replacementCostLand;
            }),
            switchMap((replacementCostLand) => {

                if (!replacementCostLand) {
                    return throwError('Could not found replacementCostLand with id of ' + id + '!');
                }

                return of(replacementCostLand);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateReplacementCostLand(replacementCostLand: ReplacementCostLand): Observable<BaseRespone<ReplacementCostLand>> {
        return this.replacementCostLands$.pipe(
            take(1),
            switchMap(replacementCostLands => this._httpClient.put<BaseRespone<ReplacementCostLand>>(`${environment.baseUrl}/Report/UpdateReplacementCostLands`, replacementCostLand).pipe(
                map((replacementCostLandCard) => {
                    const index = replacementCostLands.findIndex(item => item.id === replacementCostLand.id);

                    // Update the product
                    if (replacementCostLandCard.isSuccess) {
                        replacementCostLands[index] = replacementCostLandCard.result;

                        // Update the products
                        this._replacementCostLands.next(replacementCostLands);

                        this._replacementCostLand.next(replacementCostLandCard.result);
                    }
                    // Return the updated card
                    return replacementCostLandCard;
                }), catchError((error) => {
                    return of(error.error);
                })
            ))
        );
    }

    deleteReplacementCostLand(id: number): Observable<BaseRespone<boolean>> {
        return this.replacementCostLands$.pipe(
            take(1),
            switchMap(replacementCostLands => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteReplacementCostLands/${id}`).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    // Find the index of the deleted product
                    const index = replacementCostLands.findIndex(item => item.id === id);

                    // Delete the product
                    replacementCostLands.splice(index, 1);

                    // Update the products
                    this._replacementCostLands.next(replacementCostLands);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );
    }

    deleteAllReplacementCostLand(ids: number[]): Observable<BaseRespone<boolean>> {
        const requestBody = { ids: ids };

        return this.replacementCostLands$.pipe(
            take(1),
            switchMap(replacementCostLand => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteAllReplacementCostsLand`, {
                body: requestBody
            }).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    ids.forEach(id => {
                        // Find the index of the deleted product
                        const index = replacementCostLand.findIndex(item => item.id === id);
                        // Delete the product
                        replacementCostLand.splice(index, 1);
                    });
                    // Update the products
                    this._replacementCostLands.next(replacementCostLand);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );

    }

}
